import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  Accordion,
  ACCORDION_VARIANT_LIGHT,
  Flex,
  FlexItem,
  HelpIcon,
  ICON_FA_VARIANT_SOLID,
  Link,
  LINK_VARIANT_DEFAULT,
  Panel,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  Spinner,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT,
  Title,
  TITLE_VARIANT_M
} from 'mailup-web-components'

import { notifyParent } from 'mailup-react-helpers'

import { fetchSpamAssassinCallback } from 'Pages/MessageCheckupSpamCheck/recoil/callbacks/callbacks'
import { spamAssassinState } from 'Pages/MessageCheckupSpamCheck/recoil/state/page'

import { openHelp } from 'Helpers/openHelp'
import { useConditions } from 'mailup-react-helpers'
import renderReport from 'Pages/MessageCheckupSpamCheck/helpers/renderReport'
import { IconOk, IconSpam } from './Icons'

const BoxSpamAssassin = ({ idMessage }) => {
  const intl = useIntl()
  const [isWhiteLabel] = useConditions(['co:WHITELABEL'])

  const { loading, spamCheck } = useRecoilValue(spamAssassinState)
  const fetchSpamAssassin = useRecoilCallback(fetchSpamAssassinCallback, [])

  useEffect(() => {
    fetchSpamAssassin(idMessage)
  }, [])

  const handleHelpClick = () => {
    notifyParent('openHelp', [openHelp('spamcheck')])
  }

  return (
    <Panel>
      <Flex
        alignItems='center'
        justifyContent='space-between'
        margin='0 0 24px 0'
      >
        <FlexItem>
          <Flex>
            <Title variant={TITLE_VARIANT_M}>
              {intl.formatMessage({
                id: 'Load.EmailCheckUp.SpamCheckSpamAssassinTitle'
              })}
            </Title>
            <HelpIcon
              text={intl.formatMessage({
                id: 'Load.EmailCheckUp.SpamCheckSpamAssassinTooltip'
              })}
            />
          </Flex>
        </FlexItem>

        <Link
          variant={LINK_VARIANT_DEFAULT}
          onClick={handleHelpClick}
          data-mu-test='spamassassin-help'
          style={isWhiteLabel ? { display: 'none' } : {}}
        >
          {intl.formatMessage({
            id: 'Load.EmailCheckUp.SpamCheckSpamAssassinHelp'
          })}
        </Link>
      </Flex>
      <Flex alignItems='center' margin='0 0 24px 0' columnGap='32px'>
        <FlexItem>
          <Flex columnGap='8px' alignItems='flex-end' height='24px'>
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage({
                id: 'Load.EmailCheckUp.SpamCheckSpamAssassinCheckResult'
              })}
            </Text>
            {loading && <Spinner />}
            {!loading && spamCheck.IsSpam && (
              <IconSpam
                variant={ICON_FA_VARIANT_SOLID}
                data-mu-test='spamassassin-is-spam'
              />
            )}
            {!loading && !spamCheck.IsSpam && (
              <IconOk
                variant={ICON_FA_VARIANT_SOLID}
                data-mu-test='spamassassin-is-not-spam'
              />
            )}
          </Flex>
        </FlexItem>

        <FlexItem>
          <Flex columnGap='8px' alignItems='flex-end' height='24px'>
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage(
                {
                  id: 'Load.EmailCheckUp.SpamCheckScore'
                },
                { strong: chunks => <strong>{chunks}</strong> }
              )}
            </Text>
            {loading && <Spinner />}
            {!loading && (
              <span data-mu-test='spamassassin-score'>
                {intl.formatNumber(spamCheck.Score)}
              </span>
            )}
          </Flex>
        </FlexItem>

        <FlexItem>
          <Flex columnGap='8px' alignItems='flex-end' height='24px'>
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage(
                {
                  id: 'Load.EmailCheckUp.SpamCheckThreshold'
                },
                { strong: chunks => <strong>{chunks}</strong> }
              )}
            </Text>
            {loading && <Spinner />}
            {!loading && (
              <span data-mu-test='spamassassin-threshold'>
                {intl.formatNumber(spamCheck.Threshold)}
              </span>
            )}
          </Flex>
        </FlexItem>
      </Flex>

      <Accordion
        label={intl.formatMessage({
          id: 'Load.EmailCheckUp.SpamCheckSpamAssassinMoreInfo'
        })}
        variant={ACCORDION_VARIANT_LIGHT}
      >
        <Flex
          margin='24px 0 0 24px'
          rowGap='8px'
          flexDirection='column'
          data-mu-test='spamassassin-report'
        >
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: 'Load.EmailCheckUp.SpamCheckReportTitleDetails'
            })}
          </Text>
          <FlexItem>
            {loading ? (
              <>
                <SkeletonLoader
                  variant={SKELETON_LOADER_VARIANT_ONWHITE}
                  width='25%'
                />
                <SkeletonLoader
                  variant={SKELETON_LOADER_VARIANT_ONWHITE}
                  width='75%'
                />
              </>
            ) : (
              <Text variant={TEXT_VARIANT_LIGHT}>
                {renderReport(spamCheck.Report)}
              </Text>
            )}
          </FlexItem>
        </Flex>
      </Accordion>
    </Panel>
  )
}

BoxSpamAssassin.propTypes = {
  idMessage: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired
}

export default BoxSpamAssassin
